<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * `target_at` is stored as a UTC ISO instant, but <input type="datetime-local">
 * speaks the editing admin's local time. These two convert between them, and
 * the help text below shows the resolved instant so a tenant in one timezone
 * scheduling for another is never guessing.
 */
const localValue = computed(() => {
  const raw = props.block.data.target_at
  if (!raw) return ''
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
})

const setLocal = (value: string | number) => {
  const d = new Date(String(value))
  props.block.data.target_at = Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

const resolvedHelp = computed(() => {
  const raw = props.block.data.target_at
  if (!raw) return t('site.f.countdown.help_empty', 'Counts down to this moment, then shows the ended message.')
  const d = new Date(raw)
  if (Number.isNaN(d.getTime())) return t('site.f.countdown.help_invalid', 'That date could not be read.')
  return `${t('site.f.countdown.help_prefix', 'Counts down to')} ${d.toUTCString()} ${t('site.f.countdown.help_suffix', '(UTC). Visitors see it in their own local time.')}`
})
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>
  <UFormField :label="t('site.f.common.intro_optional', 'Intro (optional)')">
    <UTextarea
      v-model="block.data.subtext"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.countdown.counts_down_to', 'Counts down to')"
    :help="resolvedHelp"
    :error="!block.data.target_at ? t('site.f.countdown.err_target_at', 'This block needs a date before the page can be saved.') : undefined"
  >
    <UInput
      :model-value="localValue"
      type="datetime-local"
      class="w-full"
      @update:model-value="setLocal($event)"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.countdown.when_it_ends_show', 'When it ends, show')"
    :help="t('site.f.countdown.replaces_the_digits_once_the_date_passes_help', 'Replaces the digits once the date passes.')"
  >
    <UInput
      :model-value="block.data.expired_text ?? ''"
      :placeholder="t('site.f.countdown.we_re_live', 'We\'re live!')"
      class="w-full"
      @update:model-value="block.data.expired_text = String($event)"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.countdown.unit_labels', 'Unit labels')"
    :help="t('site.f.countdown.leave_blank_for_days_hours_minutes_secon_help', 'Leave blank for Days / Hours / Minutes / Seconds.')"
  >
    <div class="grid grid-cols-2 gap-2">
      <UInput
        :model-value="block.data.labels?.days ?? ''"
        :placeholder="t('site.f.countdown.days', 'Days')"
        class="w-full"
        @update:model-value="block.data.labels = { ...block.data.labels, days: String($event) }"
      />
      <UInput
        :model-value="block.data.labels?.hours ?? ''"
        :placeholder="t('site.f.countdown.hours', 'Hours')"
        class="w-full"
        @update:model-value="block.data.labels = { ...block.data.labels, hours: String($event) }"
      />
      <UInput
        :model-value="block.data.labels?.minutes ?? ''"
        :placeholder="t('site.f.countdown.minutes', 'Minutes')"
        class="w-full"
        @update:model-value="block.data.labels = { ...block.data.labels, minutes: String($event) }"
      />
      <UInput
        :model-value="block.data.labels?.seconds ?? ''"
        :placeholder="t('site.f.countdown.seconds', 'Seconds')"
        class="w-full"
        @update:model-value="block.data.labels = { ...block.data.labels, seconds: String($event) }"
      />
    </div>
  </UFormField>

  <div class="grid grid-cols-2 gap-2">
    <UFormField :label="t('site.f.common.button_text', 'Button text')">
      <UInput
        :model-value="block.data.button_text ?? ''"
        class="w-full"
        @update:model-value="block.data.button_text = String($event)"
      />
    </UFormField>
    <UFormField :label="t('site.f.common.button_url_label', 'Button URL')">
      <UInput
        :model-value="block.data.button_url ?? ''"
        :placeholder="t('site.f.common.url_placeholder', 'https://...')"
        class="w-full"
        @update:model-value="block.data.button_url = String($event)"
      />
    </UFormField>
  </div>
</template>
